import { SpendingTrendsElement, SpendingTrendsFilter } from '@core/domain/model/consumption/spending-trends.model';
import { SpendingTrendsApiFilter, SpendingTrendsApiResponse } from '@core/api/model/consumption/spending-trends-api.model';
import {
  AccumulatedConsumptionApi,
  ConsumptionFilterApi,
  YearlyAccumulatedConsumptionApi,
} from '@core/api/model/consumption/consumption-api.model';
import { addMinutes, addYears, isWithinInterval } from 'date-fns';
import { DateUtil } from '@core/services/date/date.util';
import { partition } from 'lodash-es';

export class SpendingTrendsMapper {
  public static filterToSpendingApi(domain: SpendingTrendsFilter): SpendingTrendsApiFilter {
    return {
      AnioEmisionDesde: domain.start.getFullYear(),
      AnioEmisionHasta: domain.end.getFullYear(),
      MesEmisionDesde: domain.start.getMonth() + 1,
      MesEmisionHasta: domain.end.getMonth() + 1,
      Instalaciones: domain.installations.map((value) => value.cups),
    };
  }

  public static filterToCurrentAccumulatedApi(domain: SpendingTrendsFilter): ConsumptionFilterApi {
    return {
      ListaCups: domain.installations.map((value) => value.cups),
      FechaDesde: addMinutes(domain.start, DateUtil.timeZoneDifference(domain.start)).toISOString(),
      FechaHasta: addMinutes(domain.end, DateUtil.timeZoneDifference(domain.end)).toISOString(),
    };
  }

  public static filterToPreviousAccumulatedApi(domain: SpendingTrendsFilter): ConsumptionFilterApi {
    return {
      ListaCups: domain.installations.map((value) => value.cups),
      FechaDesde: addYears(addMinutes(domain.start, DateUtil.timeZoneDifference(domain.start)), -1).toISOString(),
      FechaHasta: addYears(addMinutes(domain.end, DateUtil.timeZoneDifference(domain.end)), -1).toISOString(),
    };
  }

  public static apiSpendingToDomain(
    filter: SpendingTrendsFilter,
    api: SpendingTrendsApiResponse
  ): {
    current: SpendingTrendsElement[];
    previous: SpendingTrendsElement[];
  } {
    const allValues = api.flatMap(({ anio, gastos }) =>
      gastos.map(({ mes, importe }) => {
        return {
          value: importe,
          date: new Date(anio, mes - 1, 1, 0, 0, 0, 0),
        };
      })
    );
    const partitionValues = partition(allValues, (value) => isWithinInterval(value.date, filter));
    return {
      current: partitionValues.at(0) ?? [],
      previous: partitionValues.at(1) ?? [],
    };
  }

  public static apiAccumulatedToDomain(api: AccumulatedConsumptionApi<YearlyAccumulatedConsumptionApi>): SpendingTrendsElement[] {
    const consumos = api.flatMap((value) => value.consumos).flatMap((value) => value.datosConsumos);
    const elementsMap = new Map<string, SpendingTrendsElement>();
    consumos.forEach(({ anio, mes, consumo }) => {
      const key = `${anio}-${mes}`;
      const valueMap = elementsMap.get(key)?.value ?? 0;
      elementsMap.set(key, {
        date: new Date(Number(anio), Number(mes) - 1, 1, 0, 0, 0, 0),
        value: valueMap + consumo,
      });
    });
    return Array.from(elementsMap.values()).map((element) => ({ ...element, value: Math.round(element.value * 100) / 100 }));
  }
}
